// Pure part of „Dokument bearbeiten“: which fields a type has, the form ↔ API mapping and the PATCH body with only
// the changed fields (backend: PATCH compta/documents/:id, docs/API.md). Money stays decimal strings / BigInt cents.
import { parseUserAmount, toCents, toDecimal } from '../../api/money';
import { dmy, isoOf, money, perOf, perTxt } from '../../screens/import/format';
import { DOCUMENT_TYPE_SPECS, documentTypeHas, type DocumentField, type PayMode } from '@rimon/contracts';
import type { DocType, DocStatus } from '../../data/status';

export interface DetailFile { fileId: string; role: string; file?: { originalFilename?: string | null; mimeType?: string | null; sizeBytes?: string | number } | null }
export interface DetailPayment { id: string; amount: string; paidAt: string; communication: string | null; bankAccountId: string; corporateCardId: string | null }
export interface Reconciled { transactionId: string; statementId: string; bookingDate: string; amount: string; month: string; account: string | null }
/** GET compta/documents/:id (fields the dialog uses). */
export interface DocDetail {
  id: string; version: number; type: DocType; status: DocStatus;
  documentNumber: string | null; issuerName: string | null; employeeId?: string | null;
  documentDate: string | null; dueDate: string | null; accountingMonth?: string | null;
  netAmount?: string | null; vatAmount?: string | null; totalAmount: string | null;
  /** PAYROLL: gross pay („Brut“); netAmount = net, totalAmount = „À verser“. */
  grossAmount?: string | null;
  communication: string | null; notes?: string | null;
  /** Made in another program (rimochantiers): read-only here except status/note; link back. */
  externalSource?: string | null; externalUrl?: string | null;
  /** LP: IBAN on the invoice and its warning. */
  iban?: string | null; ibanAlert?: 'new' | 'changed' | null;
  invoiceDetail: { hasDiscount: boolean; discountRate: string | null; discountDeadline: string | null; payMode?: PayMode | null; amountToPay?: string | null } | null;
  vatLines?: Array<{ rate: string; netAmount: string; vatAmount: string }>;
  files?: DetailFile[]; payments?: DetailPayment[]; reconciledWith?: Reconciled[];
  /** Total − credit notes − escompte = paid (read-only). */
  settlement?: import('../../data/compta').Settlement | null;
  /** Credit note: the invoices it was used for (the same allocation rows the invoice sees). */
  usedFor?: Array<{ invoiceId: string; number: string | null; issuerName: string | null; amount: string }>;
}

export type Field = 'number' | 'party' | 'employee' | 'docDate' | 'dueDate' | 'month' | 'amounts' | 'lines' | 'payslip' | 'comm' | 'escompte' | 'toPay' | 'notes';
/** The shared core every editable type has; the rest comes from DOCUMENT_TYPE_SPECS (@rimon/contracts). */
const CORE: Field[] = ['number', 'docDate', 'month', 'amounts', 'notes'];
const FROM_SPEC: Partial<Record<DocumentField, Field>> = { party: 'party', employee: 'employee', dueDate: 'dueDate', netVat: 'lines', payslip: 'payslip', communication: 'comm', escompte: 'escompte', toPay: 'toPay' };
/** Fields per type (status, files and payment are separate sections). */
export const FIELDS: Record<DocType, Field[]> = Object.fromEntries((Object.keys(DOCUMENT_TYPE_SPECS) as DocType[]).map((t) => {
  const spec = DOCUMENT_TYPE_SPECS[t];
  if (!spec.fields.length) return [t, []]; // statements are edited under Abschluss
  return [t, [...CORE, ...spec.fields.map((f) => FROM_SPEC[f]).filter((f): f is Field => !!f)]];
})) as Record<DocType, Field[]>;
export const has = (t: DocType, f: Field) => FIELDS[t].includes(f);

export interface Line { rate: string; net: string; vat: string }
export interface PayForm { id: string; amount: string; paidAt: string; bank: string; card: string; comm: string }
export interface Form {
  number: string; party: string; employee: string; docDate: string; dueDate: string; month: string;
  net: string; vat: string; total: string; gross: string; lines: Line[]; comm: string; notes: string;
  hasDiscount: boolean; rate: string; deadline: string; status: DocStatus; pay: PayForm | null;
  /** „Montant à payer“: chosen mode ('' = suggestion) and the typed amount for „Anderer Betrag“. */
  payMode: PayMode | ''; toPayCustom: string;
}

const day = (v?: string | null) => (v ? dmy(v.slice(0, 10)) : '');
const rateTxt = (r?: string | null) => (r == null || r === '' ? '' : String(Number(r)).replace('.', ','));

/** API document → form texts (dates TT.MM.JJJJ, month MM/JJJJ, amounts „1.234,56“). */
export function toForm(d: DocDetail, bankOf: (accountId: string) => string = () => ''): Form {
  const p = d.payments?.length ? [...d.payments].sort((a, b) => (a.paidAt < b.paidAt ? 1 : -1))[0]! : null;
  return {
    number: d.documentNumber ?? '', party: d.issuerName ?? '', employee: d.employeeId ?? '',
    docDate: day(d.documentDate), dueDate: day(d.dueDate), month: d.accountingMonth ? perTxt(d.accountingMonth.slice(0, 7)) : '',
    net: money(d.netAmount), vat: money(d.vatAmount), total: money(d.totalAmount), gross: money(d.grossAmount),
    lines: (d.vatLines ?? []).map((l) => ({ rate: rateTxt(l.rate), net: money(l.netAmount), vat: money(l.vatAmount) })),
    comm: d.communication ?? '', notes: d.notes ?? '',
    hasDiscount: !!d.invoiceDetail?.hasDiscount, rate: rateTxt(d.invoiceDetail?.discountRate), deadline: day(d.invoiceDetail?.discountDeadline),
    status: d.status,
    payMode: d.invoiceDetail?.payMode ?? '', toPayCustom: d.invoiceDetail?.payMode === 'CUSTOM' ? money(d.invoiceDetail.amountToPay) : '',
    pay: p ? { id: p.id, amount: money(p.amount), paidAt: day(p.paidAt), bank: bankOf(p.bankAccountId), card: p.corporateCardId ?? '', comm: p.communication ?? '' }
      // Marked paid but no payment recorded (Notion „Payé“ without „Payée le“): an empty payment to add („Zahlung nachtragen“).
      : d.status === 'PAID' && documentTypeHas(d.type, 'payment') ? { id: '', amount: money(d.settlement?.due ?? d.totalAmount), paidAt: '', bank: 'BIL', card: '', comm: d.communication ?? '' }
      : null,
  };
}

export type Errors = Partial<Record<string, string>>;
const amt = (v: string) => (v.trim() ? parseUserAmount(v) : null);
const rateOf = (v: string) => { const t = v.replace('%', '').replace(',', '.').trim(); return !t ? null : /^\d{1,2}(\.\d{1,2})?$/.test(t) ? String(Number(t)) : undefined; };

/**
 * PATCH body with the changed fields only (texts trimmed, empty → null), plus per-field errors for invalid input.
 * `body` is null when nothing changed.
 */
export function buildPatch(type: DocType, a: Form, b: Form): { body: Record<string, unknown> | null; errors: Errors } {
  const body: Record<string, unknown> = {}, errors: Errors = {};
  const text = (k: keyof Form, api: string) => { if (String(a[k]).trim() !== String(b[k]).trim()) body[api] = String(b[k]).trim() || null; };
  const date = (k: 'docDate' | 'dueDate' | 'deadline', api: string) => {
    if (a[k].trim() === b[k].trim()) return;
    const v = b[k].trim(); if (!v) { body[api] = null; return; }
    const iso = isoOf(v); if (!iso) errors[k] = 'TT.MM.JJJJ'; else body[api] = iso;
  };
  const money2 = (k: 'net' | 'vat' | 'total' | 'gross', api: string) => {
    if (a[k].trim() === b[k].trim()) return;
    const v = amt(b[k]); if (v === undefined) errors[k] = '1.234,56'; else body[api] = v;
  };
  if (has(type, 'number')) text('number', 'documentNumber');
  if (has(type, 'party')) text('party', 'issuerName');
  if (has(type, 'employee') && a.employee !== b.employee) body.employeeId = b.employee || null;
  if (has(type, 'docDate')) date('docDate', 'documentDate');
  if (has(type, 'dueDate')) date('dueDate', 'dueDate');
  if (has(type, 'month') && a.month.trim() !== b.month.trim()) { const v = b.month.trim(); if (!v) body.accountingMonth = null; else { const p = perOf(v); if (!p) errors.month = 'MM/JJJJ'; else body.accountingMonth = p; } }
  if (has(type, 'amounts')) { money2('net', 'netAmount'); money2('vat', 'vatAmount'); money2('total', 'totalAmount'); }
  if (has(type, 'payslip')) money2('gross', 'grossAmount');
  if (has(type, 'lines') && JSON.stringify(a.lines) !== JSON.stringify(b.lines)) {
    const out: Array<{ rate: string; netAmount: string; vatAmount: string }> = [];
    b.lines.forEach((l, i) => {
      if (!l.rate.trim() && !l.net.trim() && !l.vat.trim()) return;
      const r = rateOf(l.rate), n = amt(l.net), v = amt(l.vat);
      if (!r) errors['line' + i] = '%'; else if (!n || !v) errors['line' + i] = '1.234,56'; else out.push({ rate: r, netAmount: n, vatAmount: v });
    });
    body.vatLines = out;
  }
  if (has(type, 'comm')) text('comm', 'communication');
  if (has(type, 'notes')) text('notes', 'notes');
  if (has(type, 'escompte')) {
    if (a.hasDiscount !== b.hasDiscount) body.hasDiscount = b.hasDiscount;
    if (a.rate.trim() !== b.rate.trim()) { const r = rateOf(b.rate); if (r === undefined) errors.rate = '%'; else body.discountRate = r; }
    date('deadline', 'discountDeadline');
  }
  if (has(type, 'toPay')) {
    if (a.payMode !== b.payMode) body.payMode = b.payMode || null;
    if (b.payMode === 'CUSTOM' && (a.toPayCustom.trim() !== b.toPayCustom.trim() || a.payMode !== 'CUSTOM')) {
      const v = amt(b.toPayCustom); if (!v || toCents(v) < 0n) errors.toPayCustom = '1.234,56'; else body.amountToPay = v;
    }
  }
  if (a.status !== b.status) body.status = b.status;
  if (b.pay && !b.pay.id) {
    // New payment: only once a date is entered (until then the section is just an offer).
    if (b.pay.paidAt.trim()) {
      const v = amt(b.pay.amount), iso = isoOf(b.pay.paidAt);
      if (!v || toCents(v) <= 0n) errors.payAmount = '1.234,56';
      if (!iso) errors.payDate = 'TT.MM.JJJJ';
      if (v && iso && toCents(v) > 0n) body.payment = { amount: v, paidAt: iso, bank: b.pay.bank || 'BIL', ...(b.pay.card ? { corporateCardId: b.pay.card } : {}), ...(b.pay.comm.trim() ? { communication: b.pay.comm.trim() } : {}) };
    }
  } else if (a.pay && b.pay) {
    const p: Record<string, unknown> = {};
    if (a.pay.amount.trim() !== b.pay.amount.trim()) { const v = amt(b.pay.amount); if (!v || toCents(v) <= 0n) errors.payAmount = '1.234,56'; else p.amount = v; }
    if (a.pay.paidAt.trim() !== b.pay.paidAt.trim()) { const iso = isoOf(b.pay.paidAt); if (!iso) errors.payDate = 'TT.MM.JJJJ'; else p.paidAt = iso; }
    if (a.pay.bank !== b.pay.bank && b.pay.bank) p.bank = b.pay.bank;
    if (a.pay.card !== b.pay.card) p.corporateCardId = b.pay.card || null;
    if (a.pay.comm.trim() !== b.pay.comm.trim()) p.communication = b.pay.comm.trim() || null;
    if (Object.keys(p).length) body.payment = { paymentId: b.pay.id, ...p };
  }
  return { body: Object.keys(body).length ? body : null, errors };
}

/** Net + VAT of the lines (cents), for the „aus Zeilen übernehmen“ hint; null when a line is not a valid amount. */
export function lineSums(lines: Line[]): { net: string; vat: string; total: string } | null {
  let n = 0n, v = 0n;
  for (const l of lines) {
    if (!l.net.trim() && !l.vat.trim()) continue;
    const a = amt(l.net), b = amt(l.vat);
    if (!a || !b) return null;
    n += toCents(a); v += toCents(b);
  }
  return { net: money(toDecimal(n)), vat: money(toDecimal(v)), total: money(toDecimal(n + v)) };
}

/** What makes a change sensitive (banner + confirmation). */
export function sensitivity(d: DocDetail): { paid: boolean; reconciled: boolean; closed: boolean } {
  return { paid: d.status === 'PAID' || !!d.payments?.length, reconciled: !!d.reconciledWith?.length, closed: d.status === 'ARCHIVED' };
}
